import * as ToggleGroup from '@radix-ui/react-toggle-group';
import Link from 'next/link';
import { useState } from 'react';

import { NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID } from '@/constants/addresses';
import { CHAIN_IDS, CHAIN_IDS_SUPPORT_INTENTS, NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { AvatarStack, getStackedAvatarStyle } from '@/ui/Avatars/AvatarStack';
import { ChainAvatar, chainAvatarRadius } from '@/ui/Avatars/ChainAvatar';
import { MatchaGreenIcon } from '@/ui/Icons/MatchaGreenIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { tradeRoute } from '@/utils/url';

import { useMatchaWallets } from '../../../hooks/useMatchaWallets';
import { getConnectedChain } from '../../../utils/wallet';
import {
  StyledH2,
  StyledParagraph,
  StyledPrimaryCell,
  StyledSecondaryCell,
} from '../../SwapTokens/utils';
import { MatchaAutoSwitchOff } from './MatchaAutoSwitchOff';
import { MatchaAutoSwitchOn } from './MatchaAutoSwitchOn';

type ToggleOption = 'on' | 'off';

export function MatchaAutoSwitchSection() {
  const { ethereumWallet } = useMatchaWallets();
  const chainId = getConnectedChain(ethereumWallet)?.id ?? CHAIN_IDS.MAINNET;
  const [isActive, setIsActive] = useState(true);
  const selectedOption: ToggleOption = isActive ? 'on' : 'off';

  const handleOnValueChange = () => {
    setIsActive(!isActive);
  };

  return (
    <StyledPrimaryCell className="flex flex-col desktop:flex-row py-14 tablet:py-7 desktop:justify-between px-6 desktop:pt-7 desktop:px-14">
      <div className="flex flex-col items-center desktop:items-start text-center desktop:text-left gap-6 mb-[6px] tablet:mb-0 mx-auto desktop:mx-0 desktop:mt-8 max-w-[500px]">
        <StyledH2 className="max-w-[270px]">Make the switch to Matcha Auto</StyledH2>
        <StyledParagraph>
          Your trading autopilot companion. When available, it takes care of all the complex
          execution settings of your swap, enables gas-free trading, and ensures you get the most
          out of your trade.
        </StyledParagraph>
        <StyledParagraph className="inline-block items-center max-w-[275px] tablet:max-w-none">
          Available on{' '}
          <AvatarStack
            className="inline-flex mr-1 relative top-[2px]"
            size={16}
            overlap={2}
            borderRadius={chainAvatarRadius}
          >
            {CHAIN_IDS_SUPPORT_INTENTS.map((chainId, index) => (
              <MatchaTooltip key={chainId}>
                <MatchaTooltipTrigger>
                  <span>
                    <ChainAvatar
                      chainId={chainId}
                      alt={`${NETWORK_NAME_PER_CHAIN_ID[chainId]} logo`}
                      size={16}
                      style={
                        index > 0 ? getStackedAvatarStyle(16, chainAvatarRadius, 2) : undefined
                      }
                    />
                  </span>
                </MatchaTooltipTrigger>
                <MatchaTooltipContent>{NETWORK_NAME_PER_CHAIN_ID[chainId]}</MatchaTooltipContent>
                <MatchaTooltipPopover sideOffset={5} />
              </MatchaTooltip>
            ))}
          </AvatarStack>
          for supported trading pairs.
        </StyledParagraph>
        <Link
          href={tradeRoute({
            address: NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId],
            chainId,
          })}
        >
          <div className={buttons({ size: 'medium', color: 'black', shape: 'pill' })}>
            Trade now
          </div>
        </Link>
      </div>

      <StyledSecondaryCell className="rounded-lg mt-20 tablet:mt-14 desktop:mt-0 tablet:mb-6 mx-auto desktop:mx-5 py-8 px-6 w-full flex flex-col items-center text-center max-w-[478px] tablet:h-[414px]">
        <MatchaGreenIcon className="size-12 desktop:my-2 desktop:size-8" />

        <h5 className="text-lg font-medium leading-tight mt-3 font-body">Matcha Auto</h5>

        <ToggleGroup.Root
          type="single"
          value={selectedOption}
          className="bg-token-gray-300 flex my-3 p-1 rounded-[1.75rem] justify-between"
          aria-label="Choose to discover Matcha Auto features"
          onValueChange={handleOnValueChange}
        >
          <ToggleGroup.Item
            className={fontStyles({
              variant: 'LABEL_MD',
              class:
                'text-token-gray-1600 flex w-full h-7 px-4 border-none items-center rounded-lg justify-center hover opacity-100 aria-checked:bg-special-background-button-medium-secondary aria-checked:shadow-05 aria-checked:transition-all aria-checked:duration-200 aria-checked:opacity-100 disabled:text-token-gray-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-token-gray-900 disabled:hover:cursor-not-allowed disabled:hover:opacity-50 disabled:aria-checked:text-token-gray-900 disabled-aria-checked:cursor-not-allowed disabled:aria-checked:opacity-50 focus-visible:outline-2 focus-visible:outline-token-gray-400 focus:active:outline-0 mr-1',
            })}
            value="on"
          >
            On
          </ToggleGroup.Item>
          <ToggleGroup.Item
            className={fontStyles({
              variant: 'LABEL_MD',
              class:
                'text-token-gray-1600 flex w-full h-7 px-4 border-none items-center rounded-lg justify-center hover opacity-100 aria-checked:bg-special-background-button-medium-secondary aria-checked:shadow-05 aria-checked:transition-all aria-checked:duration-200 aria-checked:opacity-100 disabled:text-token-gray-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-token-gray-900 disabled:hover:cursor-not-allowed disabled:hover:opacity-50 disabled:aria-checked:text-token-gray-900 disabled-aria-checked:cursor-not-allowed disabled:aria-checked:opacity-50 focus-visible:outline-2 focus-visible:outline-token-gray-400 focus:active:outline-0',
            })}
            value="off"
          >
            Off
          </ToggleGroup.Item>
        </ToggleGroup.Root>

        {isActive ? <MatchaAutoSwitchOn /> : <MatchaAutoSwitchOff />}
      </StyledSecondaryCell>
    </StyledPrimaryCell>
  );
}
